import React, { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Radar as RadarIcon, Orbit, Activity } from "lucide-react";
import { Card, Stat } from "./shared";

const LAYER_FILL = ["#ff6b35", "#c2410c", "#7c2d12", "#ffb703"];
const MARS_R = 3390;

export function radarDepth(twoWayNs, dielectric) {
    return (0.299792458 * twoWayNs) / (2 * Math.sqrt(dielectric));
}

function InteriorSection({ interior }) {
    return (
        <div className="flex items-center gap-6 flex-wrap" data-testid="geophysics-interior">
            <svg viewBox="0 0 200 200" className="w-44 h-44 shrink-0">
                {interior.map((l, i) => (
                    <circle key={l.layer} cx="100" cy="100" r={((MARS_R - l.depth_from_km) / MARS_R) * 96} fill={LAYER_FILL[i]} stroke="#0b0d14" strokeWidth="1" />
                ))}
            </svg>
            <div className="space-y-2 flex-1 min-w-[200px]">
                {interior.map((l, i) => (
                    <div key={l.layer} className="flex items-start gap-2.5">
                        <span className="w-3 h-3 rounded-sm mt-1 shrink-0" style={{ background: LAYER_FILL[i] }} />
                        <div>
                            <p className="text-sm font-medium">{l.layer} <span className="font-mono text-[10px] text-ares-muted">{l.depth_from_km}–{l.depth_to_km} km</span></p>
                            <p className="text-xs text-ares-muted">{l.note}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function RadarDepthTool({ defaultEps, reflector }) {
    const [ns, setNs] = useState(reflector ? Math.round((reflector * 2 * Math.sqrt(defaultEps)) / 0.2998) : 500);
    const [eps, setEps] = useState(defaultEps);
    const depth = radarDepth(Number(ns) || 0, eps);
    const material = eps <= 3.5 ? "Ice-rich ground" : eps <= 5.5 ? "Ice/rock mix or porous regolith" : "Dense rock / basalt";
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-4">
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-ares-muted">Two-way travel time (ns)</label>
                    <Input type="number" min="0" value={ns} onChange={(e) => setNs(e.target.value)} className="mt-1.5 bg-ares-panel border-ares-border" data-testid="radar-time-input" />
                </div>
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-ares-muted">Real dielectric constant ε′ = <span className="text-ares-bright" data-testid="radar-eps-value">{eps.toFixed(1)}</span></label>
                    <Slider value={[eps]} min={2} max={10} step={0.1} onValueChange={(v) => setEps(v[0])} className="mt-3" data-testid="radar-eps-slider" />
                    <p className="text-[11px] text-ares-muted mt-2">Pure ice ≈ 3.1 · basalt ≈ 8–9</p>
                </div>
            </div>
            <div className="rounded-lg border border-ares-cyan/30 bg-ares-cyan/5 p-4 flex flex-col justify-center">
                <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted">Reflector depth  d = c·t / 2√ε′</p>
                <p className="font-heading text-4xl font-bold text-ares-cyan mt-1" data-testid="radar-depth-result">{depth.toFixed(1)} m</p>
                <p className="text-sm text-ares-bright mt-2" data-testid="radar-material">{material}</p>
            </div>
        </div>
    );
}

export default function GeophysicsPanel({ data, interior }) {
    const g = data.geophysics;
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title="Regional geophysical signature" icon={Activity} testid="geophysics-stats-card">
                <div className="grid grid-cols-2 gap-3">
                    <Stat label="Crust thickness" value={g.crust_thickness_km} unit="km" testid="geophysics-crust" />
                    <Stat label="Free-air gravity" value={g.gravity_anomaly_mgal} unit="mGal" testid="geophysics-gravity" />
                    <Stat label="Crustal magnetism" value={g.magnetic_nT} unit="nT" testid="geophysics-magnetic" />
                    <Stat label="Heat flow" value={g.heat_flow_mw_m2} unit="mW/m²" testid="geophysics-heatflow" />
                </div>
                <p className="text-sm text-ares-muted mt-4 leading-relaxed" data-testid="geophysics-note">{g.seismic_note}</p>
            </Card>
            <Card title="Mars interior (InSight)" icon={Orbit} testid="geophysics-interior-card">
                <InteriorSection interior={interior} />
            </Card>
            <Card title="Radar sounding depth estimator" icon={RadarIcon} accent="text-ares-cyan" testid="geophysics-radar-card" className="lg:col-span-2">
                <RadarDepthTool key={data.region_id} defaultEps={g.dielectric} reflector={g.radar_reflector_depth_m} />
            </Card>
        </div>
    );
}
